import { BACKEND_URL } from "@/lib/backendUrl";
import { useCallback, useEffect, useState } from "react";
import { Download, FileSignature, Loader2, Send, Plus } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader,
  DialogTitle } from "@/components/ui/dialog";
import { Chip, Panel } from "@/components/common/Chrome";
import { api, apiError } from "@/lib/api";

const API_BASE = `${BACKEND_URL}/api`;
const words = (s) => String(s || "").replace(/_/g, " ");
const STATUS = {
  draft: "border-amber-500/30 bg-amber-500/10 text-amber-700 dark:text-amber-300",
  signed: "border-emerald-500/30 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300",
};

export const ECMinutes = () => {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(null);
  const [dialog, setDialog] = useState(null);
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");

  const load = useCallback(async (keep) => {
    try {
      const res = await api.get("/ec/minutes");
      setData(res.data);
      const fresh = res.data.items.find((m) => m.minute_ref === keep);
      if (fresh) setOpen(fresh);
    } catch (e) { setError(apiError(e)); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const run = async (key, fn, message) => {
    setBusy(key);
    try {
      const res = await fn();
      toast.success(message);
      if (res?.data?.minute_ref) setOpen(res.data);
      await load(res?.data?.minute_ref);
      setDialog(null);
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(""); }
  };

  if (error) return (
    <Panel title="Minute sheets" testId="panel-minutes-closed">
      <p className="text-[12.5px] text-muted-foreground" data-testid="minutes-refused">{error}</p>
    </Panel>
  );
  if (!data) return (
    <Panel title="Minute sheets" testId="panel-minutes-loading">
      <p className="text-[12.5px] text-muted-foreground">Opening the minute book…</p>
    </Panel>
  );

  const picked = dialog?.picked || [];

  return (
    <div className="grid gap-4 lg:grid-cols-[minmax(0,360px)_minmax(0,1fr)]">
      <Panel title="Minute sheets" testId="panel-minutes"
             kicker={`${data.items.length} sheet(s) · ${data.ready.length} to minute`}
             actions={data.may_draw && (
               <Button size="sm" className="h-8 rounded-full bg-primary text-primary-foreground hover:bg-primary/90"
                       data-testid="draw-minute" disabled={data.ready.length === 0}
                       onClick={() => setDialog({ kind: "draw", picked: [], title: "",
                         note: "" })}>
                 <Plus className="mr-1.5 h-3.5 w-3.5" /> Draw up a sheet
               </Button>
             )}>
        {data.items.length === 0 ? (
          <p className="text-[12.5px] text-muted-foreground" data-testid="minutes-empty">
            No minute sheet yet. Gather the decided matters into one sheet, sign it and send it out.
          </p>
        ) : (
          <div className="space-y-2">
            {data.items.map((m) => (
              <button key={m.minute_ref} type="button" data-testid={`minute-${m.minute_ref}`}
                      onClick={() => setOpen(m)}
                      className={`flex w-full flex-col gap-1 rounded-xl border px-3.5 py-3 text-left transition-colors duration-150 ${
                        open?.minute_ref === m.minute_ref
                          ? "border-primary/50 bg-primary/5" : "border-border hover:border-primary/30"}`}>
                <span className="flex items-center justify-between gap-2">
                  <span className="text-[12.5px] font-medium text-foreground">{m.title}</span>
                  <Chip className={STATUS[m.status]}>{words(m.status)}</Chip>
                </span>
                <span className="text-[10.5px] text-muted-foreground">
                  {m.minute_ref} · {m.matters.length} matter(s) ·{" "}
                  {(m.signatures || []).length}/2 signed
                  {m.circulated_at ? ` · sent to ${m.circulated_to}` : ""}
                </span>
              </button>
            ))}
          </div>
        )}
        {data.ready.length > 0 && (
          <div className="mt-4">
            <p className="kicker">Decided and waiting to be minuted</p>
            <div className="mt-1.5 space-y-0.5" data-testid="minutes-ready">
              {data.ready.map((r) => (
                <p key={r.item_ref} className="text-[11.5px] text-muted-foreground">
                  {r.item_ref} · {r.title} — {words(r.outcome)} on {r.decided_at}
                </p>
              ))}
            </div>
          </div>
        )}
      </Panel>

      {open ? (
        <Panel title={open.title} testId="panel-minute-open"
               kicker={`${open.minute_ref} · drawn by ${open.drawn_by} on ${open.created_at.slice(0, 10)}`}
               actions={(
                 <div className="flex flex-wrap items-center gap-2">
                   <Chip className={STATUS[open.status]} testId="minute-status">
                     {words(open.status)}
                   </Chip>
                   {open.you_may_sign && (
                     <Button size="sm" className="h-8 rounded-full bg-primary text-primary-foreground hover:bg-primary/90"
                             data-testid="sign-minute"
                             onClick={() => setDialog({ kind: "sign", minute: "" })}>
                       <FileSignature className="mr-1.5 h-3.5 w-3.5" /> Sign it
                     </Button>
                   )}
                   {open.you_may_circulate && (
                     <Button size="sm" variant="outline" className="h-8 rounded-full"
                             data-testid="circulate-minute" disabled={busy === "send"}
                             onClick={() => run("send",
                               () => api.post(`/ec/minutes/${open.minute_ref}/circulate`),
                               "The sheet is on its way")}>
                       {busy === "send" ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                         : <Send className="mr-1.5 h-3.5 w-3.5" />} Send it round
                     </Button>
                   )}
                   <a href={`${API_BASE}/ec/minutes/${open.minute_ref}/pdf`} target="_blank"
                      rel="noreferrer" data-testid="minute-pdf">
                     <Button size="sm" variant="outline" className="h-8 rounded-full">
                       <Download className="mr-1.5 h-3.5 w-3.5" /> PDF
                     </Button>
                   </a>
                 </div>
               )}>
          {open.note && (
            <p className="text-[12.5px] leading-relaxed text-muted-foreground">{open.note}</p>
          )}
          <div className="mt-3 space-y-3">
            {open.matters.map((m, index) => (
              <div key={m.item_ref} className="rounded-xl border border-border px-3.5 py-3"
                   data-testid={`minute-matter-${m.item_ref}`}>
                <p className="text-[12.5px] font-medium text-foreground">
                  {index + 1}. {m.title}
                </p>
                <p className="text-[10.5px] text-muted-foreground">
                  {m.item_ref} · {m.category_label} · raised by {m.raised_by} ({m.raised_as}) ·
                  decided {m.decided_at}
                </p>
                <p className="mt-1 text-[12px] text-foreground">
                  {words(m.outcome?.status).toUpperCase()} — {m.outcome?.for} for,{" "}
                  {m.outcome?.against} against, {m.outcome?.abstain} abstained of{" "}
                  {m.outcome?.eligible} · quorum{" "}
                  {m.outcome?.quorum_met ? "met" : "not met"}
                </p>
                <div className="mt-1 space-y-0.5">
                  {(m.votes || []).map((v) => (
                    <p key={`${m.item_ref}-${v.name}`} className="text-[11px] text-muted-foreground">
                      {v.name} ({v.office}) voted {v.decision}
                      {v.minute ? ` — ${v.minute}` : ""}
                    </p>
                  ))}
                </div>
              </div>
            ))}
          </div>
          <div className="mt-4">
            <p className="kicker">Signatures</p>
            <div className="mt-1.5 space-y-0.5">
              {(open.signatures || []).map((s) => (
                <p key={s.office} className="text-[12px] text-muted-foreground"
                   data-testid={`minute-signature-${s.office.split(" ")[0].toLowerCase()}`}>
                  {s.name} — {s.office} · signed {s.signed_at.slice(0, 10)}
                  {s.minute ? ` — ${s.minute}` : ""}
                </p>
              ))}
              {(open.waiting_on || []).map((o) => (
                <p key={o} className="text-[12px] text-muted-foreground"
                   data-testid="minute-waiting">
                  Waiting on the {o}
                </p>
              ))}
              {open.circulated_at && (
                <p className="text-[12px] text-muted-foreground" data-testid="minute-circulated">
                  Sent to {open.circulated_to} people on {open.circulated_at.slice(0, 10)} by{" "}
                  {open.circulated_by}
                </p>
              )}
            </div>
          </div>
        </Panel>
      ) : (
        <Panel title="Pick a sheet" testId="panel-minute-none">
          <p className="text-[12.5px] text-muted-foreground">
            Choose a sheet to read the matters, the voting record and who has signed.
          </p>
        </Panel>
      )}

      <Dialog open={dialog?.kind === "draw"} onOpenChange={(o) => !o && setDialog(null)}>
        <DialogContent className="max-w-lg" data-testid="draw-minute-dialog">
          <DialogHeader><DialogTitle>Draw up a minute sheet</DialogTitle>
            <DialogDescription>
              Pick the decided matters to gather. The President and the Secretary both sign before it
              goes round.
            </DialogDescription></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label className="text-[11.5px] text-muted-foreground">Title</Label>
              <Input data-testid="minute-title" value={dialog?.title || ""}
                     placeholder="Executive Committee minute, August 2026"
                     onChange={(e) => setDialog({ ...dialog, title: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label className="text-[11.5px] text-muted-foreground">Matters to gather</Label>
              <div className="max-h-52 space-y-1 overflow-y-auto rounded-xl border border-border p-2">
                {data.ready.map((r) => (
                  <label key={r.item_ref}
                         className="flex items-start gap-2 text-[11.5px] text-muted-foreground">
                    <input type="checkbox" className="mt-0.5"
                           data-testid={`pick-${r.item_ref}`}
                           checked={picked.includes(r.item_ref)}
                           onChange={(e) => setDialog({ ...dialog,
                             picked: e.target.checked ? [...picked, r.item_ref]
                               : picked.filter((x) => x !== r.item_ref) })} />
                    <span>
                      <span className="text-foreground">{r.title}</span> · {r.item_ref} ·{" "}
                      {words(r.outcome)} on {r.decided_at}
                    </span>
                  </label>
                ))}
              </div>
            </div>
            <Textarea rows={3} data-testid="minute-note"
                      placeholder="Anything to open the sheet with"
                      value={dialog?.note || ""}
                      onChange={(e) => setDialog({ ...dialog, note: e.target.value })} />
          </div>
          <DialogFooter>
            <Button data-testid="draw-minute-save"
                    disabled={busy === "draw" || picked.length === 0}
                    className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90"
                    onClick={() => run("draw", () => api.post("/ec/minutes", {
                      title: dialog.title, note: dialog.note, item_refs: picked }),
                      "The sheet is drawn up")}>
              {busy === "draw" && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Draw it up
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={dialog?.kind === "sign"} onOpenChange={(o) => !o && setDialog(null)}>
        <DialogContent className="max-w-lg" data-testid="sign-minute-dialog">
          <DialogHeader><DialogTitle>Sign the minute sheet</DialogTitle>
            <DialogDescription>
              You sign in your own office. Once both the President and the Secretary have signed, the
              sheet can go round to the committee.
            </DialogDescription></DialogHeader>
          <Textarea rows={3} data-testid="sign-minute-note" placeholder="Anything for the record"
                    value={dialog?.minute || ""}
                    onChange={(e) => setDialog({ ...dialog, minute: e.target.value })} />
          <DialogFooter>
            <Button data-testid="sign-minute-save" disabled={busy === "sign"}
                    className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90"
                    onClick={() => run("sign",
                      () => api.post(`/ec/minutes/${open.minute_ref}/sign`,
                        { minute: dialog.minute || "" }), "Your signature is on the sheet")}>
              {busy === "sign" && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Sign it
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
